// Pure math. No React, no DOM. Easy to reason about and test.

import { countries, OFFSITE_CAP } from '../data/countries';
import { convert, CURRENCY_INFO } from '../data/exchangeRates';

const num = (v, fallback = 0) => {
  const n = parseFloat(v);
  return isNaN(n) ? fallback : n;
};

const round = (val) => Number(val.toFixed(2));

// Etsy's Jun 22, 2026 regulatory-fee changes affect 7 countries (UK, France,
// Italy, Spain, Hungary, Türkiye, India). Each country's data has both the
// pre-flip rate (`regulatory`) and the post-flip rate
// (`regulatoryAfterJun22_2026`). This helper picks the right one based on
// today's date so we don't have to remember to manually swap them on launch
// day. Accepts an optional `now` for testability.
const REGULATORY_FLIP_TIMESTAMP_UTC = Date.UTC(2026, 5, 22); // June (month idx 5), 22
function getEffectiveRegulatoryRate(country, now = Date.now()) {
  if (
    now >= REGULATORY_FLIP_TIMESTAMP_UTC &&
    typeof country.regulatoryAfterJun22_2026 === 'number'
  ) {
    return country.regulatoryAfterJun22_2026;
  }
  return country.regulatory;
}

/**
 * Run the full Etsy pricing calculation.
 *
 * @param {object} inputs - User-entered values (price, costs, quantity, etc.)
 * @param {object} settings - Country, offsite ads toggle, offsite rate
 * @returns {object} Full breakdown: revenue, fees, costs, profit, margin, insights data
 */
export function calculate(inputs, settings) {
  const country = countries[settings.country] || countries.US;
  const isBulk = settings.orderType === 'bulk';

  // Display currency: defaults to country's native currency. Percentages are
  // currency-agnostic, but the country's flat fee (e.g. €0.30 EU processing)
  // and Offsite Ads $100 cap need to be converted to the display currency
  // so totals add up correctly.
  const displayCcy = settings.displayCurrency || country.code;
  const flatFeeInDisplay = convert(country.processing.fixed, country.code, displayCcy);
  const offsiteCapInDisplay = convert(OFFSITE_CAP, 'USD', displayCcy);
  const listingFeePerUnit = convert(0.20, 'USD', displayCcy);
  const displaySymbol = (CURRENCY_INFO[displayCcy] && CURRENCY_INFO[displayCcy].symbol) || country.symbol;

  const p = num(inputs.price);
  const s = num(inputs.shipping);
  const sc = num(inputs.shippingCost);
  const c = num(inputs.cost);
  const q = Math.max(1, Math.floor(num(inputs.quantity, 1)));
  const d = num(inputs.discount);

  // New cost inputs
  const laborHours = num(inputs.laborHours);
  const laborRate = num(inputs.laborRate);
  const packaging = num(inputs.packaging);
  const returnRate = Math.min(1, Math.max(0, num(inputs.returnRate) / 100));

  // Number of separate transactions/orders.
  // Bulk: one buyer, one transaction → 1 order.
  // Separate: q different buyers, q transactions → q orders.
  const orders = isBulk ? 1 : q;

  // Discount (always per item — buyer-side mechanic)
  const subtotal = p * q;
  const discountAmount = inputs.discountType === 'dollar'
    ? d * q
    : subtotal * (d / 100);

  // Revenue: shipping is charged per-order. In bulk, that's once. In separate,
  // q times. (Buyer pays for shipping per shipment, not per item.)
  const shippingCharged = s * orders;
  const revenue = subtotal - discountAmount + shippingCharged;

  // Listing fee: per-item, in display currency (Etsy's $0.20 → ~€0.19, ~£0.16)
  const listingFee = listingFeePerUnit * q;

  // Transaction fee: 6.5% of revenue. Identical formula either way.
  const transactionFee = 0.065 * revenue;

  // Processing fee: percentage on revenue + flat fee per ORDER (not per item).
  // Flat fee is from the country's currency, converted to display currency.
  const processingFee = country.processing.pct * revenue + flatFeeInDisplay * orders;

  // Regulatory fee: percentage on revenue. Identical either way. Uses the
  // post-Jun-22-2026 rate automatically when today's date is on/after that.
  const regulatoryFee = getEffectiveRegulatoryRate(country) * revenue;

  // Offsite Ads: capped per ORDER. Cap is $100 USD by Etsy policy, converted
  // to display currency.
  let offsiteFee = 0;
  if (settings.offsiteEnabled) {
    if (isBulk) {
      offsiteFee = Math.min(revenue * settings.offsiteRate, offsiteCapInDisplay);
    } else {
      const perOrderRevenue = revenue / orders;
      const perOrderFee = Math.min(perOrderRevenue * settings.offsiteRate, offsiteCapInDisplay);
      offsiteFee = perOrderFee * orders;
    }
  }

  const totalEtsyFees = listingFee + transactionFee + processingFee + regulatoryFee + offsiteFee;

  // Production-side costs: scale with q (you make q items either way)
  const productionCost = c * q;
  const packagingCost = packaging * q;
  const laborCost = laborHours * laborRate * q;

  // Shipping cost YOU pay: scales with the number of shipments, not items.
  // Bulk: one bigger package. Separate: q packages.
  // (User can override the per-shipment number if bulk shipping is heavier.)
  const shippingCostPaid = sc * orders;

  const totalCost = productionCost + shippingCostPaid + packagingCost + laborCost + totalEtsyFees;

  const grossProfit = revenue - totalCost;

  // Returns adjustment: each returned sale loses unit cost (materials, packaging,
  // labor, shipping you paid). Most Etsy fees refund on returns; we assume that.
  const unitCostPerItem = c + packaging + (laborHours * laborRate) + sc;
  const grossPerItem = grossProfit / q;
  const expectedPerItem = (1 - returnRate) * grossPerItem - returnRate * unitCostPerItem;
  const expectedProfit = expectedPerItem * q;

  const finalProfit = returnRate > 0 ? expectedProfit : grossProfit;

  return {
    // Fees
    listingFee: round(listingFee),
    transactionFee: round(transactionFee),
    processingFee: round(processingFee),
    regulatoryFee: round(regulatoryFee),
    offsiteFee: round(offsiteFee),
    totalEtsyFees: round(totalEtsyFees),

    // Costs
    productionCost: round(productionCost),
    shippingCostPaid: round(shippingCostPaid),
    packagingCost: round(packagingCost),
    laborCost: round(laborCost),
    totalCost: round(totalCost),

    // Top line
    revenue: round(revenue),
    totalRevenue: round(revenue), // alias for backwards-compat
    discountAmount: round(discountAmount),

    // Profit
    grossProfit: round(grossProfit),
    profit: round(finalProfit),
    margin: revenue > 0 ? round((finalProfit / revenue) * 100) : 0,

    // Tax set-aside — what you actually keep after self-employment tax
    taxRate: settings?.taxRate || 0,
    taxAmount: round((settings?.taxRate || 0) > 0 && finalProfit > 0
      ? finalProfit * (settings.taxRate / 100)
      : 0),
    afterTaxProfit: round((settings?.taxRate || 0) > 0 && finalProfit > 0
      ? finalProfit * (1 - settings.taxRate / 100)
      : finalProfit),

    // Insights data
    effectiveFeeRate: revenue > 0 ? round((totalEtsyFees / revenue) * 100) : 0,
    markup: (c + sc) > 0 ? round(p / (c + sc)) : 0,
    profitPerItem: round(finalProfit / q),
    // Hourly rate — what the maker earns per hour of their own time
    totalLaborHours: round(laborHours * q),
    profitPerHour: laborHours > 0 ? round(finalProfit / (laborHours * q)) : null,

    // Echo settings
    quantity: q,
    orders,
    orderType: isBulk ? 'bulk' : 'separate',
    countryCode: settings.country,
    currencyCode: displayCcy,
    currencySymbol: displaySymbol,
    returnRateApplied: returnRate > 0,
  };
}

/**
 * Reverse calculation: find the price needed to hit a target profit per item.
 * Uses binary search. Same approach as the original, extended to honor all the
 * new costs and fees.
 */
export function findBreakEvenPrice(inputs, settings, desiredProfitPerItem) {
  const dp = num(desiredProfitPerItem);
  if (dp <= 0) return null;

  const c = num(inputs.cost);
  const sc = num(inputs.shippingCost);
  const packaging = num(inputs.packaging);
  const laborCostPerItem = num(inputs.laborHours) * num(inputs.laborRate);
  const baseUnitCost = c + sc + packaging + laborCostPerItem;

  let low = baseUnitCost;
  let high = baseUnitCost + dp + 200;

  for (let i = 0; i < 500; i++) {
    const mid = (low + high) / 2;
    const trial = calculate({ ...inputs, price: String(mid) }, settings);
    const profitPerItem = trial.profit / trial.quantity;

    if (Math.abs(profitPerItem - dp) < 0.005) {
      return { exact: round(mid), rounded: Math.round(mid * 2) / 2 };
    }

    if (profitPerItem < dp) low = mid;
    else high = mid;
  }

  const final = (low + high) / 2;
  return { exact: round(final), rounded: Math.round(final * 2) / 2 };
}

/**
 * Find the price that hits a target profit margin (% of revenue).
 */
export function findPriceForMargin(inputs, settings, targetMarginPct) {
  const target = num(targetMarginPct);
  if (target <= 0 || target >= 100) return null;

  const c = num(inputs.cost);
  const sc = num(inputs.shippingCost);
  const packaging = num(inputs.packaging);
  const laborCostPerItem = num(inputs.laborHours) * num(inputs.laborRate);
  const baseUnitCost = c + sc + packaging + laborCostPerItem;

  let low = baseUnitCost;
  let high = baseUnitCost * 10 + 100;

  for (let i = 0; i < 500; i++) {
    const mid = (low + high) / 2;
    const trial = calculate({ ...inputs, price: String(mid) }, settings);

    if (Math.abs(trial.margin - target) < 0.05) {
      return { exact: round(mid), rounded: Math.round(mid * 2) / 2 };
    }

    if (trial.margin < target) low = mid;
    else high = mid;
  }

  const final = (low + high) / 2;
  return { exact: round(final), rounded: Math.round(final * 2) / 2 };
}

/**
 * Sensitivity table: profit at multiple quantity levels with current pricing.
 */
export function sensitivityTable(inputs, settings, quantities = [1, 5, 10, 25, 50, 100]) {
  return quantities.map(q => {
    const r = calculate({ ...inputs, quantity: q }, settings);
    return {
      quantity: q,
      revenue: r.revenue,
      totalCost: r.totalCost,
      profit: r.profit,
      margin: r.margin,
      profitPerItem: r.profitPerItem,
    };
  });
}
